import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import moment from 'moment';

import { showSuccessSnackbar } from 'modules/snackbar';
import { addAllList } from 'modules/common';
import {
  reqUrlList,
  reqUrlModify,
  svcTpCode,
  productCode,
} from 'lib/api/server';

import NewInput from 'components/atoms/NewInput';
import NewButton from 'components/molecules/NewButton';
import NewTableContainer from 'components/molecules/NewTable';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import Modal from 'components/organisms/NewModal';
import NewCommonFilter from 'components/organisms/NewCommonFilter';
import FilterCollection from 'components/organisms/FilterCollection';

const ReqSetting = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const initParams = {
    sDate: moment().format('YYYYMM01'),
    eDate: moment().format('YYYYMMDD'),
    svcTpCode: 'all',
    advrtsPrdtCode: 'all',
  };
  const [params, setParams] = useState(initParams);
  const [reFetch, setRefetch] = useState(false);

  const [initialization, setInitialization] = useState(false);

  const initList = { svcTpCode: [], advrtsPrdtCode: [] };
  const refList = useRef(initList);

  const [list, setList] = useState(initList);
  const initModal = { open: false, params: {}, modalTitle: t('수정') };
  const [modal, setModal] = useState(initModal);

  useEffect(() => {
    productCode({ fltSeq: 4 })
      .then((response) => {
        refList.current = {
          ...refList.current,
          advrtsPrdtCode: response.data.data,
        };
      })
      .catch((error) => {
        handleClickSnackbar({
          message: error,
          option: {
            type: 'error',
          },
        });
      })
      .finally(() => {
        setList({ ...refList.current });
      });

    svcTpCode()
      .then((response) => {
        refList.current = {
          ...refList.current,
          svcTpCode: response.data.data,
        };
      })
      .catch((error) => {
        handleClickSnackbar({
          message: error,
          option: {
            type: 'error',
          },
        });
      })
      .finally(() => {
        setList({ ...refList.current });
      }); // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (reFetch) {
      setRefetch(false);
    }
    // eslint-disable-next-line
  }, [reFetch]);

  /**
   * 필터 초기화
   */
  const handleOnReset = () => {
    if (JSON.stringify(params) !== JSON.stringify(initParams)) {
      setInitialization(true);
      setParams(initParams);
    }
  };

  const getParam = (newParams) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
    }
  };

  const addColumnList = (() => {
    const result = new Map();
    result.set(0, {
      id: 'MANAGE_ICONS',
      label: t('관리'),
      align: 'center',
      makeRowFunc: (data) => {
        return (
          <NewButton
            title={t('수정')}
            icon="faEdit"
            iconBtn
            onClick={(e) => handleModalOpen(data)}
            disableRipple
          />
        );
      },
    });
    return result;
  })();

  /**
   * modal 열어주는 함수
   */
  const handleModalOpen = (rowData) => {
    let newModal = { ...initModal };
    newModal = {
      ...newModal,
      open: true,
      params: {
        ...newModal.params,
        seq: rowData['REQ_SEQ'],
        advrtsPrdtCode:
          rowData['ADVRTS_PRDT_CODE'] === ''
            ? 'all'
            : rowData['ADVRTS_PRDT_CODE'],
        svcTpCode:
          rowData['SVC_TP_CODE'] === '' ? 'all' : rowData['SVC_TP_CODE'],
        name: rowData['REQ_URL_NM'] === null ? '' : rowData['REQ_URL_NM'],
      },
    };
    setModal({
      ...newModal,
    });
  };

  /**
   * 모달 닫아주는 함수
   */
  const handleModalClose = () => {
    let newModal = { ...modal };
    newModal = {
      ...newModal,
      open: false,
    };
    setModal(newModal);
  };

  /**
   * 모달에 submit 버튼을 눌렀을 때 실행되는 함수
   */
  const handleSubmit = () => {
    let newParams = { ...modal.params };
    for (const key in newParams) {
      if (newParams[key] === 'all') {
        delete newParams[key];
      }
    }

    reqUrlModify(newParams)
      .then((response) => {
        handleClickSnackbar({
          message: response.data.message,
          option: {
            type: 'success',
          },
        });
        setRefetch(true);
      })
      .catch((error) => {
        handleClickSnackbar({
          message: error,
          option: {
            type: 'error',
          },
        });
      });
    setModal({
      ...modal,
      open: false,
    });
  };

  /**
   * 모달 데이터 변경했을 때 실행되는 함수
   * @param newParams
   */
  const modalGetParam = (newParams) => {
    setModal({
      ...modal,
      params: {
        ...newParams,
      },
    });
  };

  const customized = {
    MANAGE_ICONS: { bodyStyle: { width: 50 } },
    REQ_SEQ: { merged: true },
    REQ_URL_PATH: { bodyStyle: { width: 300, wordBreak: 'break-all' } },
    ADVRTS_PRDT_CODE: { merged: true },
    SVC_TP_CODE: { merged: true },
  };

  const handleClickSnackbar = useCallback((snackbarOptions) => {
    dispatch(showSuccessSnackbar(snackbarOptions));
    //eslint-disable-next-line
  }, []);

  return (
    <>
      <NewFilterWrap handleOnReset={handleOnReset}>
        <NewCommonFilter
          initialization={initialization}
          params={params}
          getParam={getParam}
          usePltfomTpCodeFilter={false}
          useDisplayFilter={false}
          useExternalFilter={false}
        />
        <FilterCollection
          label={t('광고상품구분')}
          name="advrtsPrdtCode"
          params={params}
          value={params.advrtsPrdtCode}
          type="select"
          list={addAllList(list.advrtsPrdtCode, 'CODE_DESC', 'CODE_ID', 'all')}
          callBack={getParam}
          labelKey="CODE_DESC"
          valueKey="CODE_ID"
        />
        <FilterCollection
          label={t('서비스구분')}
          name="svcTpCode"
          params={params}
          value={params.svcTpCode}
          type="select"
          list={addAllList(list.svcTpCode, 'CODE_VAL', 'CODE_ID', 'all')}
          callBack={getParam}
          labelKey="CODE_VAL"
          valueKey="CODE_ID"
        />
      </NewFilterWrap>
      <NewTableContainer
        dataReqPromise={reqUrlList}
        params={params}
        getParam={getParam}
        addColumnList={addColumnList}
        customized={customized}
        reFetch={reFetch}
        useFetch={false}
        useTotal={false}
      />

      <Modal
        open={modal.open}
        headerTitle={modal.modalTitle}
        handleClose={handleModalClose}
        confirmOnClick={handleSubmit}
        useForm
      >
        <tr>
          <th>{t('요청수URL명')}</th>
          <td>
            <NewInput
              type="tableInput"
              name="name"
              params={modal.params}
              value={modal.params.name}
              callBack={modalGetParam}
            />
          </td>
        </tr>
        <tr>
          <th>{t('광고상품구분')}</th>
          <td>
            <FilterCollection
              label={t('광고상품구분')}
              name="advrtsPrdtCode"
              params={modal.params}
              value={modal.params.advrtsPrdtCode}
              type="select"
              list={addAllList(
                list.advrtsPrdtCode,
                'CODE_DESC',
                'CODE_ID',
                'choice'
              )}
              callBack={modalGetParam}
              labelKey="CODE_DESC"
              valueKey="CODE_ID"
            />
          </td>
        </tr>
        <tr>
          <th>{t('서비스구분')}</th>
          <td>
            <FilterCollection
              label={t('서비스구분')}
              name="svcTpCode"
              params={modal.params}
              value={modal.params.svcTpCode}
              type="select"
              list={addAllList(list.svcTpCode, 'CODE_VAL', 'CODE_ID', 'choice')}
              callBack={modalGetParam}
              labelKey="CODE_VAL"
              valueKey="CODE_ID"
            />
          </td>
        </tr>
      </Modal>
    </>
  );
};

export default ReqSetting;
